import { useState, useEffect } from "react";

export default function OklahomaCityHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className={`h-9 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Get Started
            </a>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${scrolled ? "text-[#1A365D]" : "text-white"}`}></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20downtown%20Oklahoma%20City%20skyline%20at%20golden%20hour%20with%20the%20iconic%20Devon%20Tower%20and%20modern%20skyscrapers%20reflecting%20warm%20sunset%20light%2C%20Oklahoma%20River%20waterfront%20visible%2C%20clear%20sky%20with%20dramatic%20warm%20clouds%2C%20professional%20cityscape%20photography%2C%20rich%20warm%20earth%20tones%20and%20golden%20light%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=okc-hero-img&orientation=landscape"
          alt="Oklahoma City downtown skyline at sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Emergency &amp; Expedited Passport Services in Oklahoma City, OK
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
            Tinker Air Force Base deployment orders with a fast-approaching report date? Business trip to Tokyo leaving from Will Rogers World Airport? OU Health medical professionals traveling to international conferences? We help Oklahoma City residents — from military families in Midwest City to downtown energy executives to OCCC students studying abroad — get their passports fast. As a registered U.S. Department of State courier, we offer rates 30–100% lower than FedEx and Staples resellers. As fast as 24 hours. A+ BBB rated. No office visit required.
          </p>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
          >
            Start My Passport <i className="ri-arrow-right-line"></i>
          </a>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
              </div>
              <span className="font-semibold">4.9 / 5</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-award-line text-[#F6C344] text-base"></i>
              <span>A+ BBB Rating</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-calendar-check-line text-[#F6C344] text-base"></i>
              <span>20+ Years</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-shield-check-line text-[#F6C344] text-base"></i>
              <span>Registered State Dept. Courier</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-price-tag-3-line text-[#F6C344] text-base"></i>
              <span>Best Price Guarantee</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}